import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Form, Switch, Input, Select, Checkbox } from 'antd';
import { Sidebar } from '@/components/sidebar';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';

/**
 * Functionary and Ownership Drawer Component
 *
 * Dynamic drawer that changes form fields based on selected document type:
 * - questionnarie: Info message only
 * - functionary: Owner selector + YES/NO switch + conditional questions
 * - ownership: Checkbox list of owners/founders
 *
 * @param {boolean} open - Drawer visibility
 * @param {Function} onClose - Close handler
 * @param {string} type - Document type (questionnarie | functionary | ownership)
 * @param {string} companyId - Company ID
 * @param {Object} companyData - Company data including name and ownershipStructure
 */
export default function FunctionaryOwnershipDrawer({
  open,
  onClose,
  type,
  companyId,
  companyData,
}) {
  const { t } = useTranslation(['forms', 'messages', 'tables']);
  const notification = useNotify();
  const [form] = Form.useForm();

  const [isYes, setIsYes] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Entries are addressed by their position in ownershipStructure: they have no
  // id, and one person is often founder, owner and director at once, so a name
  // matched several entries and the selection could not tell them apart.
  const [selectedOwnershipMembers, setSelectedOwnershipMembers] = useState([]);

  const ownershipOptions = (companyData?.ownershipStructure || [])
    .map((owner, index) => ({ owner, index }))
    .filter(({ owner }) => owner.type !== 'authorized');
  const ownershipLabel = (owner) =>
    `${owner.name} - ${t(owner.type, { ns: 'tables' })}`;

  let cardTitle = t('functionary_ownership.drawer_functionary_title', { ns: 'forms' });
  let title = t('functionary_ownership.drawer_functionary_desc', { ns: 'forms' });

  if (type === 'ownership') {
    cardTitle = t('functionary_ownership.drawer_ownership_title', { ns: 'forms' });
    title = t('functionary_ownership.drawer_ownership_desc', { ns: 'forms' });
  }

  if (type === 'questionnarie') {
    cardTitle = t('functionary_ownership.drawer_questionnaire_title', { ns: 'forms' });
    title = t('functionary_ownership.drawer_questionnaire_desc', { ns: 'forms' });
  }

  const disabled =
    type !== 'questionnarie' &&
    (type === 'ownership'
      ? selectedOwnershipMembers.length < 1
      : selectedOwner == null);

  const handleSwitchChange = (checked) => {
    setIsYes(checked);
  };

  const handleOwnerChange = (value) => {
    setSelectedOwner(value);
  };

  const handleOwnershipMembersChange = (checkedValues) => {
    setSelectedOwnershipMembers(checkedValues);
  };

  const closeDrawer = (isClose) => {
    if (!isClose) return;
    onClose();
  };

  const resetFormData = () => {
    form.resetFields();
    setIsYes(false);
  };

  const onFinish = async (_values) => {
    const payload = {
      company: companyId,
      companyName: companyData?.name,
      type: type,
      question1: isYes,
      ownershipMemberIndexes: selectedOwnershipMembers,
    };

    if (type === 'functionary') {
      Object.assign(payload, { ownerIndex: selectedOwner });
    }

    setIsLoading(true);
    try {
      const response = await API.companies.createFunctionaryOwnershipDocuments(payload);

      // Create blob and download ZIP file
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const fileName = `functionary_docs_${companyData?.name}.zip`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({
        message: t('successMessages.documents_created', { ns: 'messages' }),
        placement: 'bottomRight',
      });

      const isClose = type !== 'functionary';
      closeDrawer(isClose);
      resetFormData();
      setIsLoading(false);
    } catch (error) {
      console.error('Error creating functionary document:', error);

      let errorMessage = t('errorMessages.documents_creation_failed', { ns: 'messages' });
      let errorDescription = null;

      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.fields && errorData.fields.length > 0) {
          errorMessage = errorData.error || t('errorMessages.documents_creation_failed', { ns: 'messages' });
          errorDescription = (
            <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
              {errorData.fields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          );
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
        placement: 'bottomRight',
        duration: 0,
      });

      setIsLoading(false);
    }
  };

  return (
    <Sidebar
      title={cardTitle}
      placement="right"
      onClose={closeDrawer}
      open={open}
    >
      <div className="p-6">
        <p className="mb-4 text-sm text-gray-600">{title}</p>
        <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="form"
        >
          {type === 'ownership' && ownershipOptions.length > 0 ? (
            <Form.Item label={t('functionary_ownership.select_ownership_members', { ns: 'forms' })}>
              <Checkbox.Group
                className="flex flex-col gap-2 pl-2"
                value={selectedOwnershipMembers}
                onChange={handleOwnershipMembersChange}
              >
                {ownershipOptions.map(({ owner, index }) => (
                  <Checkbox key={index} value={index} className="ml-0">
                    {ownershipLabel(owner)}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          ) : type !== 'functionary' && type !== 'questionnarie' ? (
            <p className="text-gray-600">{t('functionary_ownership.no_ownership_structure', { ns: 'forms' })}</p>
          ) : null}

          {type === 'functionary' && (
            <>
              <Form.Item
                name="ownerSelect"
                label={t('functionary_ownership.select_owner', { ns: 'forms' })}
                className="mb-5"
              >
                <Select
                  placeholder={t('functionary_ownership.select_owner', { ns: 'forms' })}
                  onChange={handleOwnerChange}
                >
                  {ownershipOptions.map(({ owner, index }) => (
                    <Select.Option key={index} value={index}>
                      {ownershipLabel(owner)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="yesOrNo"
                label={t('functionary_ownership.is_functionary', { ns: 'forms' })}
                valuePropName="checked"
                className="mb-5"
              >
                <div className="flex justify-center">
                  <Switch
                    checked={isYes}
                    checkedChildren={t('common.yes', { ns: 'forms' })}
                    unCheckedChildren={t('common.no', { ns: 'forms' })}
                    onChange={handleSwitchChange}
                  />
                </div>
              </Form.Item>

              {isYes && (
                <>
                  <Form.Item
                    name="question2"
                    label={t('functionary_ownership.question2', { ns: 'forms' })}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item
                    name="question3"
                    label={t('functionary_ownership.question3', { ns: 'forms' })}
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item
                    name="question4"
                    label={t('functionary_ownership.question4', { ns: 'forms' })}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item
                    name="question5"
                    label={t('functionary_ownership.question5', { ns: 'forms' })}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item
                    name="question6"
                    label={t('functionary_ownership.question6', { ns: 'forms' })}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {type === 'questionnarie' && (
            <div className="space-y-3">
              <p className="text-gray-700">
                {t('functionary_ownership.questionnaire_info', { ns: 'forms' })}
              </p>
              <p className="text-gray-600">
                {t('functionary_ownership.questionnaire_reminder', { ns: 'forms' })}
              </p>
            </div>
          )}

          <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

          <div className="form-footer">
            <Button
              block
              type="primary"
              loading={isLoading}
              htmlType={!isLoading ? 'submit' : 'button'}
              className="btn-primary"
              disabled={disabled}
            >
              {t('common.submit', { ns: 'forms' })}
            </Button>
          </div>
        </Form>
      </div>
    </Sidebar>
  );
}
